import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import { Sparkle, Storefront, ArrowRight, MagnifyingGlass, Crown } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1) return "today";
  if (d < 2) return "yesterday";
  if (d < 30) return `${Math.floor(d)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function Meta({ f }) {
  return (
    <p className="text-sm text-[#8A867D]">
      {[f.era, f.maker].filter(Boolean).join(" · ") || f.category}
    </p>
  );
}

export default function CommunityFinds() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [finds, setFinds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/community/finds")
      .then(({ data }) => { setFeatured(data.featured); setFinds(data.finds || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const rest = finds.filter((f) => f.id !== featured?.id);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="community-finds-page">
      <PublicHeader />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={22} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">Fantastic Finds · from the community</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-6xl tracking-tight leading-none font-medium mb-3">
          Look what they scored
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-10">
          Real pieces our sellers spotted in the wild, identified on the spot, and grabbed before anyone else.
          This is what happens when you hunt with an expert in your pocket.
        </p>

        {loading ? (
          <p className="text-[#8A867D]">Loading finds…</p>
        ) : finds.length === 0 ? (
          <div className="border border-dashed border-[#D6D3CD] rounded-xl p-12 text-center bg-[#FBFAF6]" data-testid="community-finds-empty">
            <MagnifyingGlass size={36} weight="light" className="mx-auto text-[#B8B2A6] mb-3" />
            <p className="text-[#8A867D] mb-5">No finds shared yet — be the first to show off a score.</p>
            <button onClick={() => navigate("/finds")} data-testid="community-cta-scan"
              className="h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95">
              Start hunting <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        ) : (
          <>
            {/* Featured find of the week */}
            {featured ? (
              <div className="mb-12 grid md:grid-cols-2 gap-6 items-stretch border border-[#0D1B2A] rounded-2xl overflow-hidden bg-white" data-testid="community-featured">
                <div className="bg-[#F1EFE9] min-h-[280px]">
                  <img src={fileUrl(featured.storage_path)} alt={featured.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/vv_logo.jpg"; e.currentTarget.className = "w-full h-full object-contain opacity-30 p-6"; }} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-1.5 self-start bg-[#B47A3C] text-[#FDFBF7] text-xs font-bold px-2.5 py-1 rounded-full mb-3">
                    <Crown size={13} weight="fill" /> Find of the week
                  </span>
                  <h2 className="font-serif-display text-3xl tracking-tight leading-tight mb-1">{featured.title}</h2>
                  <Meta f={featured} />
                  {featured.caption ? <p className="text-base text-[#33302B] leading-relaxed mt-3">“{featured.caption}”</p> : null}
                  <div className="flex items-center gap-3 mt-4 text-sm">
                    <span className="font-semibold text-[#0D1B2A]">{featured.display_name}</span>
                    <span className="text-[#B8B2A6]">· {timeAgo(featured.shared_at)}</span>
                  </div>
                  {featured.best_home ? (
                    <span className="inline-flex items-center gap-1.5 self-start mt-4 bg-[#0D1B2A] text-[#FDFBF7] text-xs font-bold px-2.5 py-1 rounded-full">
                      <Storefront size={13} weight="fill" /> Best home: {featured.best_home}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="community-grid">
              {rest.map((f) => (
                <div key={f.id} className="border border-[#E5E0D6] rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow" data-testid={`community-find-${f.id}`}>
                  <div className="bg-[#F1EFE9] aspect-[4/3]">
                    <img src={fileUrl(f.storage_path)} alt={f.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/vv_logo.jpg"; e.currentTarget.className = "w-full h-full object-contain opacity-30 p-6"; }} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-bold leading-tight">{f.title}</h3>
                    <Meta f={f} />
                    {f.caption ? <p className="text-sm text-[#57534E] leading-relaxed mt-2">“{f.caption}”</p> : null}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs font-semibold text-[#0D1B2A]">{f.display_name}</span>
                      {f.best_home ? <span className="text-[11px] font-semibold text-[#B47A3C]">→ {f.best_home}</span> : <span className="text-xs text-[#B8B2A6]">{timeAgo(f.shared_at)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Funnel CTA */}
            <div className="mt-14 rounded-2xl bg-[#0D1B2A] text-[#FDFBF7] p-8 sm:p-12 text-center" data-testid="community-funnel">
              <h3 className="font-serif-display text-3xl sm:text-4xl tracking-tight mb-3">Hunt like they do.</h3>
              <p className="text-[#D6D3CD] max-w-xl mx-auto mb-6">
                Snap a photo of any piece and know instantly what it is, whether it's real, and where it sells best —
                before you spend a dime.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button onClick={() => navigate("/register")} data-testid="community-cta-start"
                  className="h-12 px-6 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-semibold inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-95">
                  Start free <ArrowRight size={16} weight="bold" />
                </button>
                <button onClick={() => navigate("/founding")} data-testid="community-cta-founding"
                  className="h-12 px-6 border border-[#FDFBF7]/40 rounded-none font-semibold inline-flex items-center gap-2 hover:bg-white/10 transition-colors">
                  <Crown size={16} weight="fill" /> Join the Founding Council
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
